import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { toast } from 'sonner';
import {
  ClipboardCheck, Star, Save, RefreshCw, CheckCircle, XCircle,
  MessageSquare, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp
} from 'lucide-react';
import axios from 'axios';

const API = '/api';

const api = axios.create({ baseURL: API });
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const StarRating = ({ score, size = 'md' }) => {
  const stars = [];
  const fullStars = Math.floor(score);
  const hasHalf = score - fullStars >= 0.5;
  const sizes = { sm: 'w-3 h-3', md: 'w-4 h-4', lg: 'w-5 h-5' };
  
  for (let i = 0; i < 5; i++) {
    if (i < fullStars) {
      stars.push(<Star key={i} className={`${sizes[size]} fill-amber-400 text-amber-400`} />);
    } else if (i === fullStars && hasHalf) {
      stars.push(<Star key={i} className={`${sizes[size]} fill-amber-400/50 text-amber-400`} />);
    } else {
      stars.push(<Star key={i} className={`${sizes[size]} text-slate-300`} />);
    }
  }
  
  return <div className="flex items-center gap-0.5">{stars}</div>;
};

const TrendIndicator = ({ value }) => {
  if (value === null || value === undefined) return <span className="text-slate-400">-</span>;
  
  if (value > 0) {
    return (
      <span className="flex items-center text-green-600 text-sm">
        <TrendingUp className="w-3 h-3 mr-1" />
        +{value.toFixed(2)}
      </span>
    );
  } else if (value < 0) {
    return (
      <span className="flex items-center text-red-600 text-sm">
        <TrendingDown className="w-3 h-3 mr-1" />
        {value.toFixed(2)}
      </span>
    );
  }
  return (
    <span className="flex items-center text-slate-500 text-sm">
      <Minus className="w-3 h-3 mr-1" />
      0
    </span>
  );
};

export const DeliveryReviewForm = ({ projectId, month, onSaved }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [comments, setComments] = useState({});
  const [generalComment, setGeneralComment] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showComments, setShowComments] = useState({});

  useEffect(() => {
    loadData();
  }, [projectId, month]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load questions
      const qRes = await api.get('/evaluation/questions');
      setQuestions(qRes.data.delivery_questions);
      
      // Load existing review
      const rRes = await api.get(`/projects/${projectId}/delivery-review/${month}`);
      if (rRes.data.status !== 'not_found') {
        const existingAnswers = {};
        const existingComments = {};
        rRes.data.answers?.forEach(a => {
          existingAnswers[a.question_id] = a.answer;
          if (a.comment) existingComments[a.question_id] = a.comment;
        });
        setAnswers(existingAnswers);
        setComments(existingComments);
        setGeneralComment(rRes.data.general_comment || '');
      }
    } catch (err) {
      toast.error('Eroare la încărcarea datelor');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const answerList = questions.map(q => ({
        question_id: q.id,
        answer: answers[q.id] || false,
        comment: comments[q.id] || null
      }));

      await api.post(`/projects/${projectId}/delivery-review`, {
        project_id: projectId,
        month: month,
        answers: answerList,
        general_comment: generalComment || null
      });

      toast.success('Review salvat cu succes!');
      if (onSaved) onSaved();
    } catch (err) {
      toast.error('Eroare la salvare');
    } finally {
      setSaving(false);
    }
  };

  const yesCount = Object.values(answers).filter(v => v).length;
  const total = questions.length;
  const score = total > 0 ? (yesCount / total) * 5 : 0;

  // Group questions by category
  const deliveryQuestions = questions.filter(q => q.category === 'delivery');
  const commQuestions = questions.filter(q => q.category === 'communication');

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
      </div>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-lg flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-blue-500" />
              Delivery Review - {month}
            </CardTitle>
            <CardDescription>
              Evaluează livrarea internă pentru această lună
            </CardDescription>
          </div>
          <div className="text-right">
            <div className="flex items-center gap-2">
              <StarRating score={score} size="lg" />
              <span className="text-xl font-bold">{score.toFixed(1)}</span>
            </div>
            <p className="text-sm text-muted-foreground">{yesCount}/{total} DA</p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Delivery & Optimization */}
        <div>
          <h4 className="font-medium text-sm text-muted-foreground mb-3">
            LIVRARE & OPTIMIZARE
          </h4>
          <div className="space-y-3">
            {deliveryQuestions.map(q => (
              <div key={q.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex-1">{q.question_ro}</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowComments(s => ({ ...s, [q.id]: !s[q.id] }))}
                      className="p-1 hover:bg-slate-100 rounded"
                    >
                      <MessageSquare className={`w-4 h-4 ${comments[q.id] ? 'text-blue-500' : 'text-slate-400'}`} />
                    </button>
                    <Switch
                      checked={answers[q.id] || false}
                      onCheckedChange={(v) => setAnswers(a => ({ ...a, [q.id]: v }))}
                    />
                    {answers[q.id] ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                </div>
                {showComments[q.id] && (
                  <Textarea
                    placeholder="Comentariu opțional..."
                    value={comments[q.id] || ''}
                    onChange={(e) => setComments(c => ({ ...c, [q.id]: e.target.value }))}
                    className="mt-2 text-sm"
                    rows={2}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Communication & Process */}
        <div>
          <h4 className="font-medium text-sm text-muted-foreground mb-3">
            COMUNICARE & PROCES
          </h4>
          <div className="space-y-3">
            {commQuestions.map(q => (
              <div key={q.id} className="border rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm flex-1">{q.question_ro}</span>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setShowComments(s => ({ ...s, [q.id]: !s[q.id] }))}
                      className="p-1 hover:bg-slate-100 rounded"
                    >
                      <MessageSquare className={`w-4 h-4 ${comments[q.id] ? 'text-blue-500' : 'text-slate-400'}`} />
                    </button>
                    <Switch
                      checked={answers[q.id] || false}
                      onCheckedChange={(v) => setAnswers(a => ({ ...a, [q.id]: v }))}
                    />
                    {answers[q.id] ? (
                      <CheckCircle className="w-5 h-5 text-green-500" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-400" />
                    )}
                  </div>
                </div>
                {showComments[q.id] && (
                  <Textarea
                    placeholder="Comentariu opțional..."
                    value={comments[q.id] || ''}
                    onChange={(e) => setComments(c => ({ ...c, [q.id]: e.target.value }))}
                    className="mt-2 text-sm"
                    rows={2}
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* General Comment */}
        <div>
          <h4 className="font-medium text-sm text-muted-foreground mb-2">
            COMENTARIU GENERAL (opțional)
          </h4>
          <Textarea
            placeholder="Note adiționale despre livrarea din această lună..."
            value={generalComment}
            onChange={(e) => setGeneralComment(e.target.value)}
            rows={3}
          />
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
            {saving ? (
              <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Save className="w-4 h-4 mr-2" />
            )}
            Salvează Review
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export const HealthScoreCard = ({ health, showHistory = false }) => {
  const [expanded, setExpanded] = useState(false);

  if (!health) {
    return (
      <Card className="bg-slate-50">
        <CardContent className="p-4 text-center text-muted-foreground">
          <p>Nu există date de evaluare pentru această lună</p>
        </CardContent>
      </Card>
    );
  }

  const statusColors = {
    excellent: 'bg-green-100 border-green-300 text-green-700',
    healthy: 'bg-blue-100 border-blue-300 text-blue-700',
    at_risk: 'bg-amber-100 border-amber-300 text-amber-700',
    critical: 'bg-red-100 border-red-300 text-red-700',
    no_data: 'bg-slate-100 border-slate-300 text-slate-700'
  };

  const statusLabels = {
    excellent: 'Excelent',
    healthy: 'Sănătos',
    at_risk: 'La Risc',
    critical: 'Critic',
    no_data: 'Fără date'
  };

  return (
    <Card className={`border-2 ${statusColors[health.health_status] || statusColors.no_data}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <div className="space-y-3 flex-1">
            {/* Health Score */}
            <div>
              <p className="text-xs font-medium text-muted-foreground">HEALTH SCORE</p>
              <div className="flex items-center gap-2 mt-1">
                {health.health_score ? (
                  <>
                    <StarRating score={health.health_score} size="md" />
                    <span className="text-lg font-bold">{health.health_score.toFixed(2)}</span>
                    <TrendIndicator value={health.health_trend} />
                  </>
                ) : (
                  <span className="text-muted-foreground">-</span>
                )}
              </div>
            </div>

            {/* Individual Scores */}
            <div className="grid grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-xs text-muted-foreground">Delivery</p>
                <div className="flex items-center gap-1">
                  <span className="font-medium">
                    {health.delivery_score?.toFixed(1) || '-'}
                  </span>
                  <TrendIndicator value={health.delivery_trend} />
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Happiness</p>
                <div className="flex items-center gap-1">
                  <span className="font-medium">
                    {health.happiness_score?.toFixed(1) || '-'}
                  </span>
                  <TrendIndicator value={health.happiness_trend} />
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">NPS</p>
                <span className="font-medium">
                  {health.nps_score !== null ? health.nps_score : '-'}
                </span>
              </div>
            </div>
          </div>

          <Badge className={statusColors[health.health_status]}>
            {statusLabels[health.health_status]}
          </Badge>
        </div>

        {/* Risk Reasons & Suggestions */}
        {(health.risk_reasons?.length > 0 || health.improvement_suggestions?.length > 0) && (
          <div className="mt-4 pt-4 border-t">
            <button
              onClick={() => setExpanded(!expanded)}
              className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-foreground"
            >
              {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
              {expanded ? 'Ascunde detalii' : 'Vezi detalii și sugestii'}
            </button>
            
            {expanded && (
              <div className="mt-3 space-y-3">
                {health.risk_reasons?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-red-600">RISCURI:</p>
                    <ul className="mt-1 space-y-1">
                      {health.risk_reasons.map((r, i) => (
                        <li key={i} className="text-sm text-red-700">• {r}</li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {health.improvement_suggestions?.length > 0 && (
                  <div>
                    <p className="text-xs font-medium text-blue-600">SUGESTII ÎMBUNĂTĂȚIRE:</p>
                    <ul className="mt-1 space-y-1">
                      {health.improvement_suggestions.map((s, i) => (
                        <li key={i} className="text-sm text-blue-700">• {s}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export const HealthHistoryChart = ({ history }) => {
  if (!history || history.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p>Nu există istoric de evaluări</p>
      </div>
    );
  }

  // Sort by month ascending for chart
  const sortedHistory = [...history].sort((a, b) => a.month.localeCompare(b.month));
  const maxScore = 5;

  return (
    <div className="space-y-4">
      {/* Simple bar chart */}
      <div className="flex items-end gap-2 h-40">
        {sortedHistory.slice(-12).map((h, i) => {
          const healthHeight = ((h.health_score || 0) / maxScore) * 100;
          const deliveryHeight = ((h.delivery_score || 0) / maxScore) * 100;
          const happinessHeight = ((h.happiness_score || 0) / maxScore) * 100;
          
          return (
            <div key={h.month} className="flex-1 flex flex-col items-center gap-1">
              <div className="flex gap-0.5 items-end h-32">
                <div 
                  className="w-2 bg-blue-500 rounded-t transition-all"
                  style={{ height: `${deliveryHeight}%` }}
                  title={`Delivery: ${h.delivery_score?.toFixed(1) || '-'}`}
                />
                <div 
                  className="w-2 bg-purple-500 rounded-t transition-all"
                  style={{ height: `${happinessHeight}%` }}
                  title={`Happiness: ${h.happiness_score?.toFixed(1) || '-'}`}
                />
                <div 
                  className="w-2 bg-green-500 rounded-t transition-all"
                  style={{ height: `${healthHeight}%` }}
                  title={`Health: ${h.health_score?.toFixed(1) || '-'}`}
                />
              </div>
              <span className="text-xs text-muted-foreground rotate-45 origin-left">
                {h.month.slice(5)}
              </span>
            </div>
          );
        })}
      </div>
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-blue-500 rounded" />
          <span>Delivery</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-purple-500 rounded" />
          <span>Happiness</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 bg-green-500 rounded" />
          <span>Health</span>
        </div>
      </div>
    </div>
  );
};

export default DeliveryReviewForm;
